import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  doc, getDoc, addDoc, updateDoc,
  collection, serverTimestamp
} from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { db, storage } from '@services/firebase';
import { ArrowLeft, Upload, X, Image, Save, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';

const STATUS_OPTIONS = [
  { value: 'live',             label: '🟢 Live' },
  { value: 'coming-soon',      label: '🔵 Coming Soon' },
  { value: 'in-development',   label: '🟣 In Development' },
];

const COLOR_OPTIONS = [
  { value: 'teal',   label: 'Teal',   cls: 'bg-teal-500' },
  { value: 'blue',   label: 'Blue',   cls: 'bg-blue-600' },
  { value: 'purple', label: 'Purple', cls: 'bg-purple-600' },
  { value: 'green',  label: 'Green',  cls: 'bg-green-600' },
  { value: 'orange', label: 'Orange', cls: 'bg-orange-500' },
  { value: 'navy',   label: 'Navy',   cls: 'bg-[#1E3A8A]' },
];

export default function AdminProjectEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const fileInputRef = useRef(null);

  const [loading, setLoading]               = useState(isEdit);
  const [saving, setSaving]                 = useState(false);
  const [uploading, setUploading]           = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [previewUrl, setPreviewUrl]         = useState('');

  const [form, setForm] = useState({
    title:        '',
    description:  '',
    tags:         '',          // comma-separated string
    emoji:        '',
    imageUrl:     '',
    url:          '',
    status:       'coming-soon',
    accentColor:  'teal',
    order:        '',
    visible:      true,
  });

  /* ── Load existing project for edit ───────────────────────────── */
  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'portfolio', id));
        if (!snap.exists()) {
          toast.error('Project not found');
          navigate('/admin/projects');
          return;
        }
        const d = snap.data();
        setForm({
          title:       d.title       || '',
          description: d.description || '',
          tags:        (d.tags || []).join(', '),
          emoji:       d.emoji       || '',
          imageUrl:    d.imageUrl    || '',
          url:         d.url         || '',
          status:      d.status      || 'coming-soon',
          accentColor: d.accentColor || 'teal',
          order:       d.order !== undefined ? String(d.order) : '',
          visible:     d.visible !== false,
        });
        if (d.imageUrl) setPreviewUrl(d.imageUrl);
      } catch {
        toast.error('Failed to load project');
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isEdit, navigate]);

  /* ── Helpers ──────────────────────────────────────────────────── */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const toggleVisible = () =>
    setForm(prev => ({ ...prev, visible: !prev.visible }));

  /* ── File upload ──────────────────────────────────────────────── */
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Only images are supported for project cover');
      return;
    }

    const folder = id || `draft_${Date.now()}`;
    const storageRef = ref(storage, `project-media/${folder}/${file.name}`);

    setUploading(true);
    setUploadProgress(0);

    const task = uploadBytesResumable(storageRef, file);
    task.on(
      'state_changed',
      (snap) => setUploadProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
      (err) => {
        console.error('Upload error:', err);
        toast.error(`Upload failed: ${err.code ?? err.message}`);
        setUploading(false);
      },
      async () => {
        const url = await getDownloadURL(task.snapshot.ref);
        setForm(prev => ({ ...prev, imageUrl: url }));
        setPreviewUrl(url);
        setUploading(false);
        toast.success('Image uploaded!');
      }
    );
  };

  const clearImage = () => {
    setForm(prev => ({ ...prev, imageUrl: '' }));
    setPreviewUrl('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleUrlChange = (e) => {
    const url = e.target.value.trim();
    setForm(prev => ({ ...prev, imageUrl: url }));
    setPreviewUrl(url);
  };

  /* ── Save ─────────────────────────────────────────────────────── */
  const handleSave = async () => {
    if (!form.title.trim()) {
      toast.error('Title is required');
      return;
    }

    setSaving(true);
    try {
      const tags = form.tags
        .split(',')
        .map(t => t.trim())
        .filter(Boolean);

      const payload = {
        title:       form.title.trim(),
        description: form.description.trim(),
        tags,
        emoji:       form.emoji.trim(),
        imageUrl:    form.imageUrl,
        url:         form.url.trim(),
        status:      form.status,
        accentColor: form.accentColor,
        order:       form.order !== '' ? Number(form.order) : null,
        visible:     form.visible,
        updatedAt:   serverTimestamp(),
      };

      if (isEdit) {
        await updateDoc(doc(db, 'portfolio', id), payload);
        toast.success('Project updated!');
      } else {
        payload.createdAt = serverTimestamp();
        await addDoc(collection(db, 'portfolio'), payload);
        toast.success('Project created!');
      }
      navigate('/admin/projects');
    } catch {
      toast.error('Failed to save project');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32 text-gray-400">
        <Loader2 size={24} className="animate-spin mr-2" /> Loading…
      </div>
    );
  }

  return (
    <div className="p-8 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <button
          onClick={() => navigate('/admin/projects')}
          className="p-2 rounded-xl hover:bg-gray-100 text-gray-500 transition-colors"
        >
          <ArrowLeft size={18} />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-800">
            {isEdit ? 'Edit Project' : 'New Project'}
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {isEdit ? 'Update project details' : 'Add a project to your portfolio'}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* ── Main fields ─────────────────────────────────────────── */}
        <div className="lg:col-span-2 space-y-5">

          {/* Title */}
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Title <span className="text-red-400">*</span>
            </label>
            <input
              name="title"
              value={form.title}
              onChange={handleChange}
              placeholder="Project name…"
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Description */}
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
            <textarea
              name="description"
              value={form.description}
              onChange={handleChange}
              rows={4}
              placeholder="Short project description shown on the homepage…"
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Tags & link */}
          <div className="bg-white rounded-2xl shadow-sm p-6 space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Tags
                <span className="ml-1 text-gray-400 font-normal text-xs">(comma-separated)</span>
              </label>
              <input
                name="tags"
                value={form.tags}
                onChange={handleChange}
                placeholder="React, Firebase, Stripe"
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Project URL</label>
              <input
                name="url"
                value={form.url}
                onChange={handleChange}
                placeholder="https://yourproject.com"
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* ── Sidebar ─────────────────────────────────────────────── */}
        <div className="space-y-5">

          {/* Publish / visibility */}
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Visibility</h3>
            <label className="flex items-center gap-3 cursor-pointer" onClick={toggleVisible}>
              <div className={`relative w-10 h-5 rounded-full transition-colors ${form.visible ? 'bg-green-500' : 'bg-gray-300'}`}>
                <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${form.visible ? 'translate-x-5' : 'translate-x-0.5'}`} />
              </div>
              <span className="text-sm text-gray-600">{form.visible ? 'Visible on site' : 'Hidden'}</span>
            </label>

            <button
              onClick={handleSave}
              disabled={saving || uploading}
              className="mt-5 w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 disabled:opacity-60 transition-colors"
            >
              {saving
                ? <><Loader2 size={14} className="animate-spin" /> Saving…</>
                : <><Save size={14} /> {isEdit ? 'Update Project' : 'Create Project'}</>
              }
            </button>
          </div>

          {/* Status */}
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Status</label>
            <select
              name="status"
              value={form.status}
              onChange={handleChange}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>

          {/* Accent color */}
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <label className="block text-sm font-semibold text-gray-700 mb-3">Accent Color</label>
            <div className="flex gap-2 flex-wrap">
              {COLOR_OPTIONS.map(c => (
                <button
                  key={c.value}
                  onClick={() => setForm(prev => ({ ...prev, accentColor: c.value }))}
                  title={c.label}
                  className={`w-7 h-7 rounded-full ${c.cls} transition-all ${form.accentColor === c.value ? 'ring-2 ring-offset-2 ring-gray-400 scale-110' : 'opacity-70 hover:opacity-100'}`}
                />
              ))}
            </div>
          </div>

          {/* Order + Emoji */}
          <div className="bg-white rounded-2xl shadow-sm p-6 space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Sort Order
                <span className="ml-1 text-gray-400 font-normal text-xs">(lower = first)</span>
              </label>
              <input
                name="order"
                type="number"
                value={form.order}
                onChange={handleChange}
                placeholder="1"
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Emoji
                <span className="ml-1 text-gray-400 font-normal text-xs">(fallback if no image)</span>
              </label>
              <input
                name="emoji"
                value={form.emoji}
                onChange={handleChange}
                placeholder="🚀"
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Cover image */}
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Cover Image</h3>

            {previewUrl ? (
              <div className="relative mb-3">
                <img
                  src={previewUrl}
                  alt="Cover"
                  className="w-full rounded-xl object-cover max-h-40"
                />
                <button
                  onClick={clearImage}
                  className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 shadow"
                >
                  <X size={12} />
                </button>
              </div>
            ) : (
              <div
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-gray-200 rounded-xl p-5 flex flex-col items-center gap-2 cursor-pointer hover:border-blue-400 hover:bg-blue-50/40 transition-colors mb-3"
              >
                <Image size={22} className="text-gray-300" />
                <p className="text-xs text-gray-400">Click to upload image</p>
              </div>
            )}

            {uploading && (
              <div className="mb-3">
                <div className="flex justify-between text-xs text-gray-500 mb-1">
                  <span>Uploading…</span>
                  <span>{uploadProgress}%</span>
                </div>
                <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-blue-500 rounded-full transition-all duration-200"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            )}

            {!uploading && (
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 border border-gray-200 rounded-xl text-xs text-gray-600 hover:bg-gray-50 transition-colors"
              >
                <Upload size={13} />
                {previewUrl ? 'Replace Image' : 'Browse Image'}
              </button>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFileUpload}
            />

            <div className="mt-3">
              <label className="block text-xs text-gray-500 mb-1">Or paste URL</label>
              <input
                value={form.imageUrl}
                onChange={handleUrlChange}
                placeholder="https://…"
                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
